/** Canonical pure semantics, shared by the host and native editor. */
import { OMNI_FREE_MODE } from './omni-duration-core';
import { cutHasVideo } from './cut-video-source-core';
import { isTrimmedRelaySource } from './cut-trim-source-core';


/** 판정 입력 — `SceneNode` 전체를 요구하지 않는다(테스트·재사용 용이 · slide-motion 과 같은 관용구). */
export interface VideoRetouchNode {
  id?: string;
  title?: string | null;
  videoUrl?: string | null;
  videoProviderUrl?: string | null;
  videoDurationSec?: number | null;
  durationSec?: number | null;
  clipStartSec?: number | null;
  videoProvider?: "sedeo" | "kling" | null;
  videoLastFrameUrl?: string | null;
  /**
   * 🔗🎬 **생성 축**(W2 · 졸업 선행 게이트 ①C) — 릴레이 소스 적격 판정의 입력.
   * ★`videoProvider` 로는 못 잰다★: 옴니 산출물은 그 값이 정직한 `null`(ARK 가 아니다)이라
   *   "옴니다"가 아니라 "모른다"에 가깝다 — 구 노드·Kling 이 같은 칸에 떨어진다. 컷이 어느 벤더로
   *   나갔는지를 아는 축은 의도 축뿐이다(`scene-inheritance` 옴니 팔과 ★같은 근거★).
   */
  genMode?: unknown;
}


/**
 * ⏱ 소스 길이 상·하한(초) — **EF 계약 미러**(`GEMINI_OMNI.editMaxSourceSec`·`GEMINI_OMNI.minDuration`).
 *   vite 번들이 EF _shared 를 직접 import 하지 않는 레포 관행(feature-keys 미러와 같은 형)이라 값을
 *   여기 두고, 동치는 `tests/unit/omni-video-retouch.test.ts` 의 미러 단언이 잠근다(한쪽만 고치면 red).
 * ★하한도 미러다★(적대 R1 · 양 렌즈 공통 P2): EF 는 3초 미만 소스를 400 으로 거절한다(하한 clamp
 *   청구는 정산이 되돌려 순손실 — `geminiOmniEditBillSeconds` 헤더). 화면이 이 축을 모르면 2초 컷이
 *   견적까지 보여준 뒤 확인창 다음에 죽는다(두 목소리).
 */
export const OMNI_RETOUCH_MAX_SOURCE_SEC = 30;


export const OMNI_RETOUCH_MIN_SOURCE_SEC = 3;


/**
 * ✍️ 벤더 프롬프트 상한 미러 — 제출 배관이 스크럽(치환으로 길이가 늘 수 있다) ★뒤★ 길이를 이 값으로
 *   한 번 더 재고, 넘으면 무과금 프리플라이트로 되돌린다(적대 R1 P3 — 확인창 뒤 EF 400 막다른 길 차단).
 * ★★이 관문은 갈래보다 ★앞★ 에 있다 — 그래서 **두 세대 상한 중 작은 쪽**이어야 한다★★
 *   (적대 R1 · G7): 관문을 통과한 문자열이 그다음에 1.0 으로도 1.1 으로도 나갈 수 있는데, 큰 쪽으로
 *   재면 **좁은 세대로 갈 때만** 확인창 뒤 EF 400 이 나는 갈래 의존 막다른 길이 생긴다.
 *   · 1.0 = `GEMINI_OMNI.maxPromptChars` = 17,500
 *   · 1.1 = `OMNI11.maxPromptChars`      = 17,500 (1.0 값의 보수 승계 — 실측 아님)
 *   ⇒ 지금은 두 값이 같아 이 상수가 곧 min 이다. **한쪽이 내려가면 이 값도 함께 내려가야** 하고,
 *     그 강제는 테스트의 `Math.min(...)` 동치 단언이 진다(잠금 없이 "같으니까 괜찮다"로 두지 않는다).
 */
export const OMNI_RETOUCH_PROMPT_MAX_CHARS = 17500;


/**
 * 💳 견적 표시용 크레딧 단가(초당) — **표시 전용 미러**다. 실제 청구의 정본은 DB `credit_costs` 이고,
 *   EF 가 서버 실측 초로 계산한다. 이 값은 확인창의 "약 N크레딧" 한 줄에만 쓰인다 — DB 단가가
 *   개정되면 이 미러도 함께 고친다(어긋나면 화면 견적만 어긋나고 청구는 안 어긋난다 · 그래서 문구가
 *   언제나 "약"이다).
 * ★정본은 이제 **두 행**이다(벤더 갈래 도입 · 아래 `omniRetouchVendorRoute`)★ — 이 한 값이 두 세대를
 *   동시에 대변할 수 있는 것은 **둘이 같은 550 이기 때문**이지 우연이 아니다:
 *   · 1.0 = `gemini-omni-flash-720p`      · 'second' · **550**(마이그레이션 20260808010000)
 *   · 1.1 = `gemini-omni-11-flash-720p`   · 'second' · **550**(마이그레이션 20260828233000)
 *   ⇒ 둘 중 한쪽 단가가 개정되면 **표시 미러 하나로는 더 이상 참을 말할 수 없다**(갈래별 견적으로
 *     쪼개야 한다). 그 동치는 테스트가 두 마이그레이션 파일에 대고 값으로 잠근다.
 */
export const OMNI_RETOUCH_CREDITS_PER_SECOND = 550;


/**
 * ✍️ 자유 입력 상한(자) — EF 프롬프트 상한(17,500 · `GEMINI_OMNI.maxPromptChars` 미러)에서
 *   래퍼(`omniRetouchFreePrompt` 의 보존 절) 예산을 뺀 값. 손으로 고르지 말고 테스트의
 *   래퍼 길이 재검산 단언이 판정하게 두라(가드 예산 관용구 — gemini-omni-contract 헤더와 같은 형).
 */
export const OMNI_RETOUCH_FREE_MAX_CHARS = 16500;


/**
 * 🔒 막힌 사유 코드 — 문구를 화면에 흩지 않고 **값**으로 든다(`SlideMotionBlockCode` 와 같은 형).
 *   · `free-mode-off`  옴니 자유 모드가 닫혔다(컷과 무관한 ★전역★ 사유 — EF 도 같은 조건을 400 으로 문다)
 *   · `no-cut`         컷 자체를 못 찾았다(선택 유실·삭제된 컷)
 *   · `no-video`       이 컷에는 아직 영상이 없다(리터치는 완성 영상을 손질하는 기능이다 — 판정은
 *                      `cutHasVideo` 하나: blob:·data: 컷은 하류가 못 받아 유료 확인창 뒤에 죽는다)
 *   · `trimmed-clip`   구간 컷(`isTrimmedRelaySource` 와 같은 술어) — 요금이 구간이 아니라 **원본 전체
 *                      길이**로 계산되고(EF 는 소스 파일 전체를 실측한다), 편집본과 구간 좌표의 정합도 미실측이다.
 *   · `relay-pending`  이 영상을 이어받을 다음 컷이 **아직 안 만들어졌다** — 지금 리터치하면 편집본은
 *                      릴레이 비적격(videoProvider:null·끝프레임 null)이 되어 그 컷의 이어붙임(참조
 *                      릴레이·자동이음 둘 다)이 소리 없이 죽는다. (편집=사슬 완결 후 한 자리 — W1 확정)
 *   · `too-short`      소스가 벤더 하한(3초) 미만이다(EF 400 미러 — 확인창 뒤 막다른 길 차단)
 *   · `too-long`       소스가 벤더 상한(30초)을 넘는다
 */
export type VideoRetouchBlockCode =
  | "free-mode-off"
  | "no-cut"
  | "no-video"
  | "trimmed-clip"
  | "relay-pending"
  | "too-short"
  | "too-long";


/** 🔐 잠금은 '잠겼나'와 '왜'를 한 값으로(레포 헌법) — `ok:true` 면 `code`·`reason` 은 언제나 null. */
export interface VideoRetouchEligibility {
  ok: boolean;
  code: VideoRetouchBlockCode | null;
  /** 화면에 **문단으로** 그대로 띄울 사유 — "왜 막혔나 + 무엇을 하면 되나"를 한 문장에 담는다. */
  reason: string | null;
}


export const OPEN: VideoRetouchEligibility = { ok: true, code: null, reason: null };

export const block = (code: VideoRetouchBlockCode, reason: string): VideoRetouchEligibility => ({ ok: false, code, reason });


/**
 * 🎞 **이 컷의 영상을 이어받는(릴레이) 다른 컷들** — 잠금·경고 판정의 재료.
 *
 * ★릴레이는 두 갈래다 — 둘 다 재야 한다★(적대 R1 · correctness-P1: 참조 갈래만 재던 초판은 이 레포의
 *   ★기본 이음★을 못 봤다):
 *   ① **자동이음(그래프 엣지)** — `planContinuationRelay(prev, scene)` 가 다음 컷 생성 때 앞 컷의
 *      `videoProvider`·`videoUrl`·`videoLastFrameUrl` 을 직접 읽는다(다음 컷에는 아무 참조도 안 남는다).
 *      호출부가 시퀀스상 다음 컷(`opts.nextCut`)을 넘겨 주면, 그 컷이 아직 영상이 없을 때 pending 으로 센다.
 *   ② **유저 재참조** — 이웃의 `references[type==="video"]` 가 내 URL 을 가리키는 경우.
 *      해소부(`resolveUserChainRef`)의 실 조건을 미러한다: 영상 참조가 ★정확히 1개★·`chainExtend !== false`.
 * ★소스 적격이 선행 조건이다★(적대 R1 · correctness-P2: 성립할 수 없는 릴레이로 잠그지 않는다) —
 *   해소부·자동이음 둘 다 `videoProvider === "sedeo"` + 미트림 + 길이 규격(`relayDurationOutOfRange`)
 *   소스만 릴레이한다. 내 컷이 그 조건 밖이면 리터치가 죽일 사슬 자체가 없다.
 * ⚠️ ★★"옴니 산출물은 사슬이 없다"는 옛 문장은 ★거짓이 됐다★ — 정정한다★★(W2 · 2026-09-02):
 *   `OMNI11_CONTINUATION_ENABLED` 가 켜지면서 옴니 컷 뒤의 이어짐 컷은 `omni11-relay` 팔로 앞 컷
 *   ★영상★ 에서 끝프레임을 그때 뽑아 쓴다(창구 3곳 — 캔버스·씬 추가·재생성/바로제작). 즉 옴니 컷도
 *   릴레이 소스이고, 리터치로 그 영상을 갈아치우면 다음 컷이 뽑을 원본이 바뀐다.
 *   ⇒ `videoRetouchRelaySourceEligible` 이 옴니 팔을 함께 문다(그 함수 머리말이 처분 전문).
 * ★`sceneExcluded` 는 **의존으로 센다**(보수 판정)★ — 그 축은 2.5+게이트 ON 에서만 실효인데 이
 *   순수부는 모델·게이트를 모른다. 돈이 걸린 판정은 넓은 쪽이 안전하다(fail-closed · 잔여 회부).
 */
export interface VideoRetouchRelayDependents {
  /** 이어받을 예정인데 **영상이 아직 없는** 컷 — 리터치를 잠그는 축. */
  pending: VideoRetouchNode[];
  /** 이미 이어받아 **영상까지 만든** 컷 — 잠그지 않고 경계 어긋남 가능성만 고지하는 축. */
  done: VideoRetouchNode[];
}


/**
 * 🚦 이 컷에 「영상 리터치」를 열 수 있는가.
 *
 * ★순서가 곧 안내의 품질이다★(slide-motion 과 같은 규약) — 전역 사유(자유 모드) → "이 컷이 대상인가"
 *   (컷·영상 유무) → "이 소스로 되는가"(구간·릴레이·길이) 순으로, 위에서 걸린 사유 하나만 말한다.
 * ★막다른 길 금지★ — 모든 사유가 수단을 함께 말한다. `free-mode-off` 만은 유저가 못 고치는 운영
 *   스위치라 그 사실을 숨기지 않고 적는다.
 *
 * @param opts.freeMode 기본값은 프론트 사본 상수(`OMNI_FREE_MODE`) — 주입 가능(플립 없이 닫힌 세계를
 *   값으로 단언 · slide-motion 과 같은 관용구). EF 가 같은 조건을 과금 전에 한 번 더 문다(이중 문).
 *   ★★게이트가 **1.0 스위치 하나만** 보는 것은 의도다(적대 R1 · G6 — 회부 유지)★★: 벤더 갈래가
 *     생겨 1.1 로 나가는 회차도 이 문을 지나는데, 그때도 판정 재료는 `OMNI_FREE_MODE` 다.
 *     ⇒ 1.0 자유 모드를 닫으면 **1.1 리터치까지 함께 닫힌다.** 이건 결함이 아니라 **fail-closed** 다:
 *       ⓐ 닫는 방향이라 유료 사고를 만들지 않는다(과잉 잠금의 대가는 "버튼이 안 열린다"뿐) ⓑ 리터치
 *       창구 자체가 옴니 자유 모드 위에 세워진 기능이라, 1.0 을 되돌리는 상황이면 이 창구를 함께
 *       접는 것이 실제로 의도한 롤백이다.
 *     ★그리고 여기 걸려 있던 **진짜 위험은 소멸했다**★ — 종전 우려는 "1.1 자유 모드가 닫히면 EF 가
 *       한글 금지 가드를 붙여 「깨진 한글 수리」 지시를 뒤집는다"였는데, W1 수리(G2)로 **edit 태스크는
 *       가드·필수 문장을 아예 부착하지 않게** 계약이 바뀌었다(`OMNI11_TASK_SPECS.edit.promptAttachments`).
 *       즉 두 스위치가 갈리지 않아 생길 수 있는 사고는 이제 "덜 열린다" 하나뿐이다.
 *     ★★그리고 회부의 값어치 자체가 내려갔다★★ — G2 로 edit 의 부착이 전부 `false` 가 되면서
 *       `OMNI11_FREE_MODE` 는 **리터치 축에서 아무것도 좌우하지 않는 무효 롤백 축**이 됐다(그 스위치가
 *       열든 닫든 리터치 제출문은 같다). 즉 게이트를 쪼개도 되돌릴 수 있게 되는 것이 없다.
 *     ⇒ 게이트를 갈래별로 쪼개는 것(1.1 회차는 `OMNI11_FREE_MODE` 를 보게)은 **회부로 남긴다** —
 *       지금 쪼개면 잠금 어휘가 갈래를 알아채기 시작하고(화면·제출 두 목소리의 입구), 얻는 것은
 *       "1.0 을 닫아도 1.1 리터치는 열려 있다"는 아직 아무도 요구하지 않은 자유도뿐이다.
 * @param opts.relay 이어받는 컷 판정(`videoRetouchRelayDependents` 산출) — 호출부(화면·핸들러)가
 *   같은 헬퍼로 계산해 넘긴다. 미전달이면 그 축은 판정하지 않는다(재료 없이 지어내지 않는다).
 */
export function videoRetouchEligibility(
  node: VideoRetouchNode | null | undefined,
  opts: { freeMode?: boolean; relay?: VideoRetouchRelayDependents } = {},
): VideoRetouchEligibility {
  if (!(opts.freeMode ?? OMNI_FREE_MODE)) {
    return block(
      "free-mode-off",
      "지금은 영상 리터치를 쓸 수 없어요 — 이 기능은 옴니 자유 모드가 열려 있는 동안에만 쓸 수 있는데 지금은 닫혀 있어요. 컷을 고쳐서 열 수 있는 건 아니라, 필요하시면 운영자에게 문의해 주세요.",
    );
  }
  if (!node) return block("no-cut", "컷을 찾지 못했어요 — 컷을 다시 선택해 주세요.");
  // 🎞 "영상이 있나"는 SSOT 하나(`cutHasVideo`) — blob:·data: 컷은 하류가 못 받으므로 여기서 닫아야
  //   유료 확인창 뒤 프리플라이트 사망(다시 눌러도 영영 안 되는 안내)이 안 생긴다.
  if (!cutHasVideo(node)) {
    return block(
      "no-video",
      "이 컷에는 아직 쓸 수 있는 완성 영상이 없어요 — 리터치는 완성된 영상을 손질하는 기능이라, 먼저 영상을 만든(또는 다시 만든) 뒤에 눌러주세요.",
    );
  }
  // ✂️ 구간 판정도 해소부와 같은 술어(`isTrimmedRelaySource`) — 손사본이면 판정과 고지가 갈린다.
  if (isTrimmedRelaySource(node)) {
    return block(
      "trimmed-clip",
      /* 🚪 ★★막다른 길을 걷어낸 문안(2026-09-01)★★ — 종전 꼬리는 「**컷 나누기**로 구간을 독립
         영상으로 만든 뒤에 눌러주세요」였다. ★시키는 대로 해도 안 된다★: 현행 컷 나누기
         (`planSplitCut`)는 같은 mp4 를 좌표로 나눠 갖는 **가상 분할**이라(`clipStartSec` +
         `videoDurationSec`) 산출 조각이 **또 구간 컷**이고, 그 조각이 이 게이트에 **다시** 막힌다.
         유저는 안내를 따를수록 같은 벽에 부딪히고, 그게 정확히 이 레포가 «막다른 길」이라 부르는 것이다.
         ⇒ **실제로 가능한 두 행동**만 말한다: ①이 구간의 출처인 원본 컷에서 고치기(그 컷은 통짜라
           열려 있다) ②이 컷을 다시 만들기(재생성 산출물은 자기 파일이라 통짜가 된다).
         ★게이트 로직은 그대로다★ — 고친 것은 «무엇을 하면 되나»뿐이고 «왜 막혔나»는 종전 그대로다. */
      "이 컷은 원본 영상의 일부 구간만 쓰는 컷이라 아직 리터치할 수 없어요 — 요금이 구간이 아니라 원본 전체 길이로 계산되기 때문이에요. 이 구간의 원본이 된 컷에서 고치거나, 이 컷을 다시 만든 뒤에 눌러주세요.",
    );
  }
  if (opts.relay && opts.relay.pending.length > 0) {
    return block(
      "relay-pending",
      "이 영상을 이어받을 다음 컷이 아직 만들어지지 않았어요 — 지금 리터치하면 그 컷의 이어붙이기가 끊어져요. 그 컷을 먼저 만든 뒤에 리터치하거나, 이어붙임 참조를 걸어 둔 컷이라면 그 참조에서 이어붙이기를 해제해 주세요.",
    );
  }
  const sec = node.videoDurationSec;
  if (typeof sec === "number" && Number.isFinite(sec) && sec > 0 && sec < OMNI_RETOUCH_MIN_SOURCE_SEC) {
    return block(
      "too-short",
      `이 영상은 ${OMNI_RETOUCH_MIN_SOURCE_SEC}초보다 짧아 편집 모델이 받지 않아요 — 영상 요금이 최소 ${OMNI_RETOUCH_MIN_SOURCE_SEC}초부터 계산돼서예요. 컷을 합치거나 ${OMNI_RETOUCH_MIN_SOURCE_SEC}초 이상으로 다시 만든 뒤에 눌러주세요.`,
    );
  }
  if (typeof sec === "number" && Number.isFinite(sec) && sec > OMNI_RETOUCH_MAX_SOURCE_SEC) {
    return block(
      "too-long",
      `이 영상은 ${Math.ceil(sec)}초라 편집 모델 최대 ${OMNI_RETOUCH_MAX_SOURCE_SEC}초를 넘어요 — 컷을 나눠 ${OMNI_RETOUCH_MAX_SOURCE_SEC}초 이하로 만든 뒤에 눌러주세요.`,
    );
  }
  return OPEN;
}


/* ── 💳 견적 ─────────────────────────────────────────────────────────────── */

export interface VideoRetouchEstimate {
  /** 견적에 쓴 초(올림) — 길이를 모르면 null(지어내지 않는다). */
  seconds: number | null;
  /** 표시용 크레딧(약) — 길이를 모르면 null. */
  credits: number | null;
  /** 확인창·패널에 그대로 띄울 한 줄. */
  notice: string;
}


/**
 * 💳 **정직 견적** — 소스 길이를 프론트가 이미 알므로(`videoDurationSec`) `ceil(초) × 초당 크레딧`을
 *   미리 말할 수 있다(실측 ③ 과금 산식 미러 — `geminiOmniEditBillSeconds` 의 ceil 과 같은 방향).
 *   ★서버 실측이 최종이다★ — 프론트 기록이 낡았으면 EF 의 400/413/402 문안이 그대로 뜬다(막다른 길
 *   아님 · 문안이 조치 수단을 담는 것은 EF 계약이 보장한다). 길이를 모르면 숫자를 지어내지 않고
 *   "서버가 재서 계산한다"고 말한다.
 */
export function omniRetouchEstimate(videoDurationSec: number | null | undefined): VideoRetouchEstimate {
  if (typeof videoDurationSec !== "number" || !Number.isFinite(videoDurationSec) || videoDurationSec <= 0) {
    // 💳 숫자 없이 동의를 받는 회차 — ★상한★ 은 벤더 계약에서 참으로 말할 수 있으므로 함께 준다
    //   (적대 R1 billing-P2: 상한 없는 백지 동의를 만들지 않는다).
    const cap = OMNI_RETOUCH_MAX_SOURCE_SEC * OMNI_RETOUCH_CREDITS_PER_SECOND;
    return {
      seconds: null,
      credits: null,
      notice: `이 영상의 길이 기록이 없어 요금을 미리 계산하지 못해요 — 서버가 영상을 받아 실제 길이로 계산하고, 잔액이 모자라면 요청 전에 알려드려요. 어떤 경우에도 최대 ${cap.toLocaleString()}크레딧(${OMNI_RETOUCH_MAX_SOURCE_SEC}초 상한)을 넘지 않아요.`,
    };
  }
  const seconds = Math.ceil(videoDurationSec);
  const credits = seconds * OMNI_RETOUCH_CREDITS_PER_SECOND;
  return {
    seconds,
    credits,
    notice: `예상 요금: 약 ${credits.toLocaleString()}크레딧 (영상 ${seconds}초 × 초당 ${OMNI_RETOUCH_CREDITS_PER_SECOND}크레딧) — 최종 요금은 서버가 실제 영상 길이를 재서 계산해요.`,
  };
}


/* ── ✍️ 지시문(프리셋 + 자유 입력) ───────────────────────────────────────── */

/** 이 창구가 제출할 지시 — 화면·핸들러가 같은 유니온을 쓴다(문자열 이중 조립 금지). */
export type VideoRetouchInstruction =
  | { kind: "preset"; presetId: VideoRetouchPresetId }
  /** `target` = 화면에 넣을 ★정확한 글자★(선택 · W1b 입력칸). 본문 따옴표 인용과 합쳐 `retouchTextTargetsOf` 가 센다. */
  | { kind: "free"; text: string; target?: string };


export type VideoRetouchPresetId = "fix-broken-hangul";


/**
 * 🩹 **「깨진 한글 수리」 프리셋 문안** — `omniSlideFreezePrompt` 의 절 구조(과업 절 → 불변 절 →
 *   NEGATIVE 절)를 승계해 **새로 설계**했다(헤더 실측 ⑤ — 실측 통과 문자열이 아니다).
 *
 * ★어휘 계약(실측 ④ 승계)★:
 *   · 위치 지시어를 한 글자도 쓰지 않는다 — `row`/`line`/`nth` 금지(테스트가 값으로 문다).
 *     지시는 "이미 인쇄돼 있는 그 글자" 형으로 **화면에 실재하는 대상**만 가리킨다.
 *   · **숫자는 고치지 않는다** — 깨진 숫자를 "수리"하는 것은 곧 금액을 지어내는 것이다(장표 동결
 *     레인이 박제한 금액 변조 실사고와 같은 축). 손상돼 보여도 그대로 두라고 명시한다.
 *   · **새 글자를 더하지 않는다** — 영상 내 글자 상시 금지 정책의 옴니 예외(OMNI_FREE_MODE 결속)는
 *     "있던 글자의 수리"까지다. 금액·완결 주장문을 새로 만드는 방향은 NEGATIVE 로 막는다.
 */
export function omniRetouchHangulRepairPrompt(): string {
  return [
    "EDIT TASK — REPAIR GARBLED KOREAN TEXT, IN PLACE ONLY",
    "This video already contains printed Korean text. Some of those characters are garbled, malformed or",
    "illegible pseudo-Korean glyphs. Re-print only those broken characters as the correct, natural Korean",
    "words they were clearly meant to be, keeping the same font, size, color, position and timing as the",
    "surrounding text. Text that is already correct must stay untouched, character for character.",
    "Do not repair, change or redraw any digit or number — if a number looks damaged, leave it exactly as it is.",
    "",
    "EVERYTHING ELSE — DO NOT CHANGE",
    "Keep every other element of every frame exactly as it is: people, faces, motion, camera work,",
    "framing, background, colors, lighting, pacing and audio all stay identical to the source video.",
    "Do not add any new text, caption, symbol, icon or watermark anywhere, and do not remove, move or",
    "restyle any element that is not a broken Korean character.",
    "",
    "NEGATIVE PROMPT — unwanted outcomes (do not produce any of these)",
    "new caption, new subtitle, watermark, logo, extra text, added sentence, translated text, re-typed",
    "paragraph, changed number, changed price, changed layout, different font, camera move, added person,",
    "added object, removed object, restyled scene",
  ].join("\n");
}


/**
 * ✍️ **자유 입력 지시문 래퍼** — 유저 지시를 그대로 싣되, "지시한 것만 바꾸고 나머지는 그대로"를
 *   같은 절 구조로 뒤에 세운다. 벤더의 edit 태스크는 보존이 기본 동작이지만(실측 ① 길이·타이밍 보존),
 *   **화면 요소 보존의 범위는 미실측**이라 절을 명시한다 — 프리셋과 같은 방향의 보수 설계다.
 * ★정책 절은 지시와 무관하게 ★항상★ 선다(적대 R1 · security-P1 수리)★: 금액·수치 변조와 완결 주장문은
 *   영상 내 글자 정책의 옴니 예외(OMNI_FREE_MODE)에서도 ★금지 유지★ 축이라(회장님 2026-08-24 확정),
 *   "지시가 요구하면 허용" 형 문장을 쓰지 않는다 — 유저가 "금액을 바꿔줘"라고 적어도 이 절이 뒤에
 *   서서 막는다(후행 절 우세 관찰과 같은 방향의 보수 배치 · 프롬프트 층 방어라는 한계는 회부 명시).
 * ★유저 원문은 검열하지 않는다★ — 스튜디오 고유명사 스크럽(`sanitizeStudioNames`)은 호출부(제출 층)가
 *   본문 전체에 태운다(생성 경로와 같은 벤더 거절 방지 축 · 문안 소유는 이 함수, 스크럽은 배관).
 */
/* ══ 🔤 목표 글자 낱개 명세(2026-09-05 실측 · 맥공유 옴니글자-실측-2026-09-05/README.md) ══════════════
 * 같은 소스(성인 씬 · 퍼즐 판 글자 깨짐)에 한국어 제품 지시는 ★무편집★(2,750 만 소진), 한글 목표는 4/6.
 * 영어로 글자를 ★낱개로 나열★하면 글자형 8/8(거울·깨짐 0 · 인물·구도 유지). 참조 이미지는 2/2 구도 파괴라
 * 제품에 넣지 않는다. 그래서 자유 지시에서 ★따옴표 안 문자열★(또는 W1b 「정확한 글자」 칸)을 목표로 삼아
 * T2 형 영어 블록을 지시 ★뒤·보존 절 앞★ 에 세운다. 본문 번역은 EF(`_shared/omni-edit-translate.ts`) 몫.
 * ★숫자가 든 목표는 제출 전에 막는다★ — 뒤의 NON-NEGOTIABLE 절이 어차피 이기므로 돈만 쓰는 경로다. */
export const OMNI_RETOUCH_TEXT_TARGET_MAX_CHARS = 40;

export const OMNI_RETOUCH_TEXT_TARGET_MAX_COUNT = 4;

/** 따옴표 6종 — «» "" “” 『』 「」 '' ‘’ (한 줄 안 · 1~80자). 글로벌 정규식은 매 호출 새로 만든다(lastIndex 오염 금지). */
export const quotedTargetRe = () => /«([^»\n]{1,80})»|"([^"\n]{1,80})"|“([^”\n]{1,80})”|『([^』\n]{1,80})』|「([^」\n]{1,80})」|'([^'\n]{1,80})'|‘([^’\n]{1,80})’/g;


/** 본문 따옴표 안 문자열을 순서대로(중복 제거 · 상한 개수) 돌려준다. */
export function extractRetouchTextTargets(text: string | null | undefined): string[] {
  const out: string[] = [];
  const re = quotedTargetRe();
  for (const m of String(text ?? "").matchAll(re)) {
    const raw = (m.slice(1).find((g) => typeof g === "string") ?? "").trim();
    if (raw && !out.includes(raw)) out.push(raw);
    if (out.length >= OMNI_RETOUCH_TEXT_TARGET_MAX_COUNT) break;
  }
  return out;
}


/** 지시의 목표 글자 전체 — 명시 칸(`target`)이 먼저, 본문 인용이 뒤(같은 값은 한 번). 프리셋은 0. */
export function retouchTextTargetsOf(instruction: VideoRetouchInstruction): string[] {
  if (instruction.kind !== "free") return [];
  const explicit = (instruction.target ?? "").trim();
  const quoted = extractRetouchTextTargets(instruction.text);
  const merged = explicit ? [explicit, ...quoted.filter((q) => q !== explicit)] : quoted;
  return merged.slice(0, OMNI_RETOUCH_TEXT_TARGET_MAX_COUNT);
}


/** 목표 글자 자체의 거절 사유(무과금 거절) — 숫자 포함·상한 초과. 정상은 null. */
export function retouchTextTargetIssue(targets: readonly string[]): string | null {
  for (const t of targets) {
    if (/[0-9０-９]/.test(t)) {
      return `숫자가 든 글자("${t}")는 고칠 수 없어요 — 금액·수치는 영상 편집으로 바꾸지 않아요. 숫자를 빼고 적어주세요.`;
    }
    if (t.length > OMNI_RETOUCH_TEXT_TARGET_MAX_CHARS) {
      return `넣을 글자가 너무 길어요("${t.slice(0, 12)}…") — 한 덩어리 ${OMNI_RETOUCH_TEXT_TARGET_MAX_CHARS}자 이내로 줄여주세요.`;
    }
  }
  return null;
}


export function spellOut(target: string): string {
  return Array.from(target).filter((ch) => !/\s/.test(ch)).map((ch) => `"${ch}"`).join(", ");
}


/** T2 형 영어 블록 — 목표가 없으면 빈 문자열(래퍼 출력이 종전과 바이트 동일). */
export function omniRetouchTextTargetBlock(targets: readonly string[]): string {
  if (!targets.length) return "";
  const lines = targets.map((t, i) => `Target ${i + 1}: “${t}” — character by character, in this order: ${spellOut(t)}.`);
  return [
    "TEXT TARGET — EXACT LETTERING",
    "The lettering the instruction above refers to must read EXACTLY as follows, and nothing else:",
    ...lines,
    "Rules for that lettering: plain bold sans-serif glyphs, upright, not mirrored, not rotated, sharp and fully",
    "legible in every frame, in exactly that order and spacing (a space in the target is a word gap), with no other",
    "letters, marks or extra lines on that surface. If a character cannot be rendered exactly, leave that surface",
    "blank instead of writing a different character.",
  ].join("\n");
}


export function omniRetouchFreePrompt(instruction: string, targets: readonly string[] = []): string {
  const targetBlock = omniRetouchTextTargetBlock(targets);
  const compose = (withTargets: boolean) => [
    "EDIT TASK — APPLY THIS INSTRUCTION ONLY",
    instruction.trim(),
    "",
    ...(withTargets && targetBlock ? [targetBlock, ""] : []),
    "EVERYTHING ELSE — DO NOT CHANGE",
    "Apply only the instruction above. Keep every other element of every frame exactly as it is: people,",
    "faces, motion, camera work, framing, background, colors, lighting, pacing and audio all stay identical",
    "to the source video. Do not add any watermark or logo.",
    "",
    "NON-NEGOTIABLE — THESE RULES OVERRIDE THE INSTRUCTION ABOVE",
    "Never invent, change or redraw any digit, number, price or amount anywhere in the video, even if the",
    "instruction asks for it. Never add any new sentence that claims completion, approval, sign-up,",
    "guarantee or a finished deal. If the instruction conflicts with these rules, follow these rules and",
    "apply only the parts of the instruction that do not.",
  ].join("\n");
  const full = compose(true);
  // 📏 예산 — 상한 길이 본문 + 목표 블록이 벤더 천장을 넘으면 블록만 접는다(본문의 따옴표는 그대로 남는다).
  return full.length <= OMNI_RETOUCH_PROMPT_MAX_CHARS ? full : compose(false);
}


/**
 * 🔎 자유 입력 검증 — 화면(누르기 전)과 핸들러(제출 전)가 같은 판정을 쓴다.
 * @returns 문제가 없으면 null, 있으면 화면에 그대로 띄울 사유.
 */
export function videoRetouchFreeTextIssue(text: string | null | undefined, target?: string | null): string | null {
  const t = typeof text === "string" ? text.trim() : "";
  if (!t) return "고칠 내용을 적어주세요 — 예: \"칠판의 깨진 글자를 자연스러운 한글로 고쳐줘\".";
  if (t.length > OMNI_RETOUCH_FREE_MAX_CHARS) {
    return `지시문이 너무 길어요 — ${OMNI_RETOUCH_FREE_MAX_CHARS.toLocaleString()}자 이내로 줄여주세요.`;
  }
  // 🔤 목표 글자(명시 칸 + 본문 인용)의 사유 — 화면·핸들러가 같은 판정(무과금 거절).
  return retouchTextTargetIssue(retouchTextTargetsOf({ kind: "free", text: t, ...(target ? { target } : {}) }));
}


/**
 * ✍️ 지시 → 벤더 프롬프트 — 문안 조립의 유일한 창구(핸들러가 문자열을 손으로 잇지 않는다).
 * @returns 조립된 프롬프트. 자유 입력이 비었으면 null(검증은 `videoRetouchFreeTextIssue` 소관).
 */
export function omniRetouchPromptOf(instruction: VideoRetouchInstruction): string | null {
  if (instruction.kind === "preset") {
    // 프리셋 id 는 열거형이라 여기서 분기한다 — 미지 id 가 타입으로 막힌다.
    return omniRetouchHangulRepairPrompt();
  }
  const t = instruction.text.trim();
  return t ? omniRetouchFreePrompt(t, retouchTextTargetsOf(instruction)) : null;
}
